import type { StepType } from "@reactour/tour"
import {
  BarChart3,
  CalendarDays,
  DollarSign,
  LayoutGrid,
  PieChart,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  TrendingDown,
  Trophy,
  Users,
} from "lucide-react"

export const getDashboardTourSteps = (t: (key: string) => string): StepType[] => [
  {
    selector: '[data-tourid="dashboard-header"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.welcomeTitle")}
        </h3>
        <p className="text-sm leading-relaxed">{t("pages.dashboard.tour.welcomeDescription")}</p>
      </div>
    ),
  },
  {
    selector: '[data-tourid="date-range-picker"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.dateRangePickerTitle")}
        </h3>
        <p className="text-sm leading-relaxed">
          {t("pages.dashboard.tour.dateRangePickerDescription")}
        </p>
      </div>
    ),
  },
  {
    selector: '[data-tourid="virtual-expenses-toggle"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.virtualExpensesToggleTitle")}
        </h3>
        <p className="text-sm leading-relaxed">
          {t("pages.dashboard.tour.virtualExpensesToggleDescription")}
        </p>
      </div>
    ),
  },
  {
    selector: '[data-tourid="refresh-button"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.refreshDataTitle")}
        </h3>
        <p className="text-sm leading-relaxed">
          {t("pages.dashboard.tour.refreshDataDescription")}
        </p>
      </div>
    ),
  },
  {
    selector: '[data-tourid="filters-button"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <Search className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.advancedFiltersTitle")}
        </h3>
        <p className="text-sm leading-relaxed">
          {t("pages.dashboard.tour.advancedFiltersDescription")}
        </p>
      </div>
    ),
  },
  {
    selector: '[data-tourid="global-summary"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.globalFinancialSummaryTitle")}
        </h3>
        <p className="text-sm leading-relaxed mb-2">
          {t("pages.dashboard.tour.globalFinancialSummaryDescription")}
        </p>
        <ul className="text-sm space-y-1.5 list-disc list-inside text-muted-foreground">
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.expenseTrend")}:</strong>{" "}
            {t("pages.dashboard.tour.expenseTrendDescription")}
          </li>
          <li>
            <strong className="text-foreground">
              {t("pages.dashboard.tour.partnerContributions")}:
            </strong>{" "}
            {t("pages.dashboard.tour.partnerContributionsDescription")}
          </li>
          <li>
            <strong className="text-foreground">
              {t("pages.dashboard.tour.remainingBalance")}:
            </strong>{" "}
            {t("pages.dashboard.tour.remainingBalanceDescription")}
          </li>
        </ul>
      </div>
    ),
  },
  {
    selector: '[data-tourid="sankey-diagram"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <RotateCcw className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.sankeyFlowDiagramTitle")}
        </h3>
        <p className="text-sm leading-relaxed mb-2">
          {t("pages.dashboard.tour.sankeyFlowDiagramDescription")}
        </p>
        <ul className="text-sm space-y-1.5 list-disc list-inside text-muted-foreground">
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.leftSide")}:</strong>{" "}
            {t("pages.dashboard.tour.leftSideDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.rightSide")}:</strong>{" "}
            {t("pages.dashboard.tour.rightSideDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.flowLines")}:</strong>{" "}
            {t("pages.dashboard.tour.flowLinesDescription")}
          </li>
        </ul>
        <p className="text-sm leading-relaxed mt-2">{t("pages.dashboard.tour.sankeyHoverHint")}</p>
      </div>
    ),
  },
  {
    selector: '[data-tourid="monthly-expenses-card"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.monthlyExpensesCardTitle")}
        </h3>
        <p className="text-sm leading-relaxed mb-2">
          {t("pages.dashboard.tour.monthlyExpensesCardDescription")}
        </p>
        <ul className="text-sm space-y-1.5 list-disc list-inside text-muted-foreground">
          <li>
            <strong className="text-foreground">
              {t("pages.dashboard.tour.currentMonthTotal")}:
            </strong>{" "}
            {t("pages.dashboard.tour.currentMonthTotalDescription")}
          </li>
          <li>
            <strong className="text-foreground">
              {t("pages.dashboard.tour.cumulativeChart")}:
            </strong>{" "}
            {t("pages.dashboard.tour.cumulativeChartDescription")}
          </li>
          <li>
            <strong className="text-foreground">
              {t("pages.dashboard.tour.dailyExpensesChart")}:
            </strong>{" "}
            {t("pages.dashboard.tour.dailyExpensesChartDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.monthlySummary")}:</strong>{" "}
            {t("pages.dashboard.tour.monthlySummaryDescription")}
          </li>
        </ul>
      </div>
    ),
  },
  {
    selector: '[data-tourid="expense-sources-card"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <PieChart className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.expenseSourcesCardTitle")}
        </h3>
        <p className="text-sm leading-relaxed mb-2">
          {t("pages.dashboard.tour.expenseSourcesCardDescription")}
        </p>
        <ul className="text-sm space-y-1.5 list-disc list-inside text-muted-foreground">
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.pieChartView")}:</strong>{" "}
            {t("pages.dashboard.tour.pieChartViewDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.listView")}:</strong>{" "}
            {t("pages.dashboard.tour.listViewDescription")}
          </li>
        </ul>
        <p className="text-sm leading-relaxed mt-2">
          {t("pages.dashboard.tour.expenseSourcesToggleHint")}
        </p>
      </div>
    ),
  },
  {
    selector: '[data-tourid="monthly-balance-card"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.monthlyBalanceCardTitle")}
        </h3>
        <p className="text-sm leading-relaxed mb-2">
          {t("pages.dashboard.tour.monthlyBalanceCardDescription")}
        </p>
        <ul className="text-sm space-y-1.5 list-disc list-inside text-muted-foreground">
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.areaChart")}:</strong>{" "}
            {t("pages.dashboard.tour.areaChartDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.monthlyList")}:</strong>{" "}
            {t("pages.dashboard.tour.monthlyListDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.hoverDetails")}:</strong>{" "}
            {t("pages.dashboard.tour.hoverDetailsDescription")}
          </li>
        </ul>
      </div>
    ),
  },
  {
    selector: '[data-tourid="partner-contribution-card"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.partnerContributionCardTitle")}
        </h3>
        <p className="text-sm leading-relaxed mb-2">
          {t("pages.dashboard.tour.partnerContributionCardDescription")}
        </p>
        <ul className="text-sm space-y-1.5 list-disc list-inside text-muted-foreground">
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.barChart")}:</strong>{" "}
            {t("pages.dashboard.tour.barChartDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.partnerList")}:</strong>{" "}
            {t("pages.dashboard.tour.partnerListDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.ratioAnalysis")}:</strong>{" "}
            {t("pages.dashboard.tour.ratioAnalysisDescription")}
          </li>
        </ul>
      </div>
    ),
  },
  {
    selector: '[data-tourid="top-expenses-card"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.topExpensesCardTitle")}
        </h3>
        <p className="text-sm leading-relaxed mb-2">
          {t("pages.dashboard.tour.topExpensesCardDescription")}
        </p>
        <ul className="text-sm space-y-1.5 list-disc list-inside text-muted-foreground">
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.rankedList")}:</strong>{" "}
            {t("pages.dashboard.tour.rankedListDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.detailsShown")}:</strong>{" "}
            {t("pages.dashboard.tour.detailsShownDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.quickInsights")}:</strong>{" "}
            {t("pages.dashboard.tour.quickInsightsDescription")}
          </li>
        </ul>
        <p className="text-sm leading-relaxed mt-2">
          {t("pages.dashboard.tour.topExpensesScrollHint")}
        </p>
      </div>
    ),
  },
  {
    selector: '[data-tourid="category-comparison-card"]',
    content: (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          {t("pages.dashboard.tour.categoryComparisonCardTitle")}
        </h3>
        <p className="text-sm leading-relaxed mb-2">
          {t("pages.dashboard.tour.categoryComparisonCardDescription")}
        </p>
        <ul className="text-sm space-y-1.5 list-disc list-inside text-muted-foreground">
          <li>
            <strong className="text-foreground">
              {t("pages.dashboard.tour.groupedBarChart")}:
            </strong>{" "}
            {t("pages.dashboard.tour.groupedBarChartDescription")}
          </li>
          <li>
            <strong className="text-foreground">
              {t("pages.dashboard.tour.colorCodedCategories")}:
            </strong>{" "}
            {t("pages.dashboard.tour.colorCodedCategoriesDescription")}
          </li>
          <li>
            <strong className="text-foreground">{t("pages.dashboard.tour.multiMonthView")}:</strong>{" "}
            {t("pages.dashboard.tour.multiMonthViewDescription")}
          </li>
        </ul>
        <p className="text-sm leading-relaxed mt-2">
          {t("pages.dashboard.tour.categoryComparisonHoverHint")}
        </p>
      </div>
    ),
  },
]

// Keep the old export for backwards compatibility, but it will use English only
export const dashboardTourSteps: StepType[] = getDashboardTourSteps((key: string) => key)
